// The axios singleton and auth-token helpers live in ./api/instance so that
// api/client.ts can import them without creating a cycle back through this
// module's `export * from './api/index'` below. See that file for details.
import api, { setAuthToken, getAuthToken } from './api/instance';

export { setAuthToken, getAuthToken };

// ─── Auth ──────────────────────────────────────────────────────────────────────

export async function login(username: string, password: string) {
  const res = await api.post('/api/v1/auth/login', { username, password });
  setAuthToken(res.data.access_token);
  localStorage.setItem('fleet_role', res.data.role);
  localStorage.setItem('fleet_driver_name', res.data.driver_name);
  if (res.data.account_id) localStorage.setItem('fleet_account_id', res.data.account_id);
  if (res.data.account_name) localStorage.setItem('fleet_account_name', res.data.account_name);
  if (res.data.account_slug) localStorage.setItem('fleet_account_slug', res.data.account_slug);

  // Import authStore dynamically to avoid circular deps
  const { useAuthStore } = await import('../stores/authStore');
  useAuthStore.getState().setAuth(
    res.data.access_token,
    res.data.role,
    res.data.driver_name,
    res.data.account_id ?? null,
    res.data.account_name ?? null,
    res.data.account_slug ?? null,
    res.data.logistics_groups ?? [],
  );
  return res.data;
}

export async function logout() {
  try { await api.post('/api/v1/auth/logout'); } catch (_) {}
  setAuthToken(null);
  localStorage.removeItem('fleet_role');
}

export function getCurrentRole(): string | null {
  return localStorage.getItem('fleet_role');
}

export async function forgotPassword(username: string) {
  const res = await api.post('/api/v1/auth/forgot-password', { username });
  return res.data;
}

export async function resetPassword(token: string, newPassword: string) {
  const res = await api.post('/api/v1/auth/reset-password', { token, new_password: newPassword });
  return res.data;
}

export async function getDeliveryAttempts(loadId: string, stopId: string) {
  const res = await api.get(`/api/v1/loads/${loadId}/stops/${stopId}/delivery-attempts`);
  return res.data;
}

export async function getProfile() {
  const res = await api.get('/api/v1/auth/profile');
  return res.data;
}

export async function updateProfile(data: { full_name?: string; email?: string; phone_number?: string; preferred_language?: string }) {
  const res = await api.patch('/api/v1/auth/profile', data);
  return res.data;
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const res = await api.post('/api/v1/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res.data;
}

// ─── Dispatch ─────────────────────────────────────────────────────────────────

export async function getActiveDrivers() {
  const res = await api.get('/api/v1/dispatch/drivers/active');
  return res.data;
}

export async function getDriverStatus(driverId: string) {
  const res = await api.get(`/api/v1/dispatch/drivers/${driverId}/status`);
  return res.data;
}

// ─── Analytics ────────────────────────────────────────────────────────────────

export async function getRouteReplay(loadId: string) {
  const res = await api.get(`/api/v1/analytics/loads/${loadId}/route`);
  return res.data;
}

export async function getDriverSummary(driverId: string, date?: string) {
  const res = await api.get(`/api/v1/analytics/drivers/${driverId}/summary`, {
    params: date ? { date } : undefined,
  });
  return res.data;
}

export async function getEnRouteLoads() {
  const res = await api.get('/api/v1/analytics/loads/en-route');
  return res.data;
}

export async function getDashboardLoads(
  statuses?: string[],
  dateFrom?: string,
  originFacilityIds?: string[],
  dateTo?: string,
  executionTiers?: string[],
) {
  const params: Record<string, string> = {};
  if (statuses && statuses.length > 0) {
    params.statuses = statuses.join(',');
  }
  // If a range is provided, send date_from+date_to. Otherwise fall back to single `date`.
  if (dateFrom && dateTo && dateFrom !== dateTo) {
    params.date_from = dateFrom;
    params.date_to = dateTo;
  } else if (dateFrom) {
    params.date = dateFrom;
  }
  if (originFacilityIds && originFacilityIds.length > 0) {
    params.origin_facilities = originFacilityIds.join(',');
  }
  if (executionTiers && executionTiers.length > 0) {
    params.execution_tiers = executionTiers.join(',');
  }
  // Always request v2 format (DashboardResponse wrapper with tier_summary + thresholds)
  params.response_format = 'v2';
  const res = await api.get('/api/v1/analytics/loads/dashboard', { params });
  return res.data;
}

/// Resolved freight display profile for the account (default ⊕ config override).
/// Drives type-aware freight rendering (container vs item) on the dashboard.
export async function getFreightDisplayProfile() {
  const res = await api.get('/api/v1/tms/freight-display-profile');
  return res.data;
}

/// Multi-driver HOS Duty Schedule Board — per-driver duty window + typed segments.
export async function getHosScheduleBoard(date?: string, logisticsGroupId?: string) {
  const params: Record<string, string> = {};
  if (date) params.date = date;
  if (logisticsGroupId) params.logistics_group_id = logisticsGroupId;
  const res = await api.get('/api/v1/hos/schedule-board', { params });
  return res.data;
}

// ── Dispatcher: Origin Facilities & Authorization ─────────────────────────

export async function getMyAuthorizedFacilities() {
  const res = await api.get('/api/v1/dispatcher/my-authorized-facilities');
  return res.data;
}

export async function getOriginFacilities() {
  const res = await api.get('/api/v1/dispatcher/origin-facilities');
  return res.data;
}

export async function getDispatcherAuthorizedFacilities(userId: string) {
  const res = await api.get(`/api/v1/dispatcher/users/${userId}/authorized-facilities`);
  return res.data;
}

export async function setDispatcherAuthorizedFacilities(
  userId: string,
  isAllFacilities: boolean,
  facilityIds: string[]
) {
  const res = await api.put(`/api/v1/dispatcher/users/${userId}/authorized-facilities`, {
    is_all_facilities: isAllFacilities,
    facility_ids: facilityIds,
  });
  return res.data;
}

// ── Dispatcher: Saved Filters ─────────────────────────────────────────────

export async function getMySavedFilters() {
  const res = await api.get('/api/v1/dispatcher/my-filters');
  return res.data;
}

export async function saveMySavedFilters(filterData: {
  selectedStatuses: string[];
  selectedOriginFacilityIds: string[];
  selectedFilterDriverId: string | null;
  selectedLogisticsGroupId: string | null;
}) {
  const res = await api.put('/api/v1/dispatcher/my-filters', { filter_data: filterData });
  return res.data;
}

export async function getLoadDebrief(loadId: string) {
  const res = await api.get(`/api/v1/analytics/loads/${loadId}/debrief`);
  return res.data;
}

export async function getRouteDeviations(loadId: string) {
  const res = await api.get(`/api/v1/analytics/loads/${loadId}/deviations`);
  return res.data;
}

export async function getDriverScorecard(driverId: string, period: string = 'DAILY', date?: string) {
  const res = await api.get(`/api/v1/analytics/drivers/${driverId}/scorecard`, {
    params: { period, ...(date ? { date } : {}) },
  });
  return res.data;
}

// ─── Admin — Users ─────────────────────────────────────────────────────────────

export async function listUsers() {
  const res = await api.get('/api/v1/admin/users');
  return res.data;
}

export async function createUser(data: object) {
  const res = await api.post('/api/v1/admin/users', data);
  return res.data;
}

export async function updateUser(userId: string, data: object) {
  const res = await api.patch(`/api/v1/admin/users/${userId}`, data);
  return res.data;
}

// ─── Admin — Drivers ──────────────────────────────────────────────────────────

export async function listDrivers(params?: { search?: string; employment_status?: string }) {
  const res = await api.get('/api/v1/admin/drivers', { params });
  return res.data;
}

export async function createDriver(data: object) {
  const res = await api.post('/api/v1/admin/drivers', data);
  return res.data;
}

export async function getDriver(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}`);
  return res.data;
}

export async function updateDriver(driverId: string, data: object) {
  const res = await api.patch(`/api/v1/admin/drivers/${driverId}`, data);
  return res.data;
}

export async function resetUserPassword(userId: string, newPassword: string) {
  const res = await api.post(`/api/v1/admin/users/${userId}/reset-password`, {
    new_password: newPassword,
  });
  return res.data;
}

export async function getDriverAssignments(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/assignments`);
  return res.data;
}

// ─── Admin — Tracking Devices ─────────────────────────────────────────────────

export async function listDevices(params?: { search?: string; status?: string }) {
  const res = await api.get('/api/v1/admin/devices', { params });
  return res.data;
}

export async function createDevice(data: object) {
  const res = await api.post('/api/v1/admin/devices', data);
  return res.data;
}

export async function getDevice(deviceId: string) {
  const res = await api.get(`/api/v1/admin/devices/${deviceId}`);
  return res.data;
}

export async function updateDevice(deviceId: string, data: object) {
  const res = await api.patch(`/api/v1/admin/devices/${deviceId}`, data);
  return res.data;
}

export async function getDeviceAssignments(deviceId: string) {
  const res = await api.get(`/api/v1/admin/devices/${deviceId}/assignments`);
  return res.data;
}

export async function assignDevice(deviceId: string, data: { driver_id: string; assigned_at: string; notes?: string }) {
  const res = await api.post(`/api/v1/admin/devices/${deviceId}/assign`, data);
  return res.data;
}

export async function unassignDevice(deviceId: string) {
  const res = await api.post(`/api/v1/admin/devices/${deviceId}/unassign`);
  return res.data;
}

// ─── Admin — Event Types ───────────────────────────────────────────────────────

export async function listEventTypes() {
  const res = await api.get('/api/v1/admin/event-types');
  return res.data;
}

export async function createEventType(data: object) {
  const res = await api.post('/api/v1/admin/event-types', data);
  return res.data;
}

export async function updateEventType(id: string, data: object) {
  const res = await api.patch(`/api/v1/admin/event-types/${id}`, data);
  return res.data;
}

// ─── Admin — Configs ──────────────────────────────────────────────────────────

export async function listConfigs() {
  const res = await api.get('/api/v1/admin/configs');
  return res.data;
}

export async function updateConfig(key: string, value: string) {
  const res = await api.patch(`/api/v1/admin/configs/${key}`, { value });
  return res.data;
}

// ─── Admin — Loads ────────────────────────────────────────────────────────────

export async function listLoads() {
  const res = await api.get('/api/v1/admin/loads');
  return res.data;
}

export async function uploadLoadsExcel(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/api/v1/admin/loads/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return res.data;
}

export async function createLoad(data: object) {
  const res = await api.post('/api/v1/admin/loads', data);
  return res.data;
}

export async function deleteLoad(id: string) {
  await api.delete(`/api/v1/admin/loads/${id}`);
}

// ─── TMS Integration ──────────────────────────────────────────────────────────

export async function listTmsProviders() {
  const res = await api.get('/api/v1/tms/providers');
  return res.data;
}

export async function createTmsProvider(data: object) {
  const res = await api.post('/api/v1/tms/providers', data);
  return res.data;
}

export async function updateTmsProvider(configId: number, data: object) {
  const res = await api.patch(`/api/v1/tms/providers/${configId}`, data);
  return res.data;
}

export async function triggerTmsSync(clientId: string) {
  const res = await api.post('/api/v1/tms/sync', { client_id: clientId });
  return res.data;
}

export async function tmshealthCheck(clientId: string) {
  const res = await api.post('/api/v1/tms/health-check', { client_id: clientId });
  return res.data;
}

export async function syncTmsByDateRange(data: { client_id: string; start_datetime: string; end_datetime: string }) {
  const res = await api.post('/api/v1/tms/sync-by-date', data);
  return res.data;
}

export async function listTmsSyncLogs(params?: { client_id?: string; limit?: number }) {
  const res = await api.get('/api/v1/tms/sync-logs', { params });
  return res.data;
}

export async function getTmsSyncLogLoads(logId: number) {
  const res = await api.get(`/api/v1/tms/sync-logs/${logId}/loads`);
  return res.data;
}

// ─── TMS Sync Agent ─────────────────────────────────────────────────────────

export async function getTmsAgentSettings(configId: number) {
  const res = await api.get(`/api/v1/tms/providers/${configId}/agent`);
  return res.data;
}

export async function updateTmsAgentSettings(configId: number, data: object) {
  const res = await api.patch(`/api/v1/tms/providers/${configId}/agent`, data);
  return res.data;
}

export async function listTmsFilterProfiles(configId: number) {
  const res = await api.get(`/api/v1/tms/providers/${configId}/filter-profiles`);
  return res.data;
}

export async function createTmsFilterProfile(configId: number, data: object) {
  const res = await api.post(`/api/v1/tms/providers/${configId}/filter-profiles`, data);
  return res.data;
}

export async function updateTmsFilterProfile(profileId: number, data: object) {
  const res = await api.patch(`/api/v1/tms/filter-profiles/${profileId}`, data);
  return res.data;
}

export async function deleteTmsFilterProfile(profileId: number) {
  const res = await api.delete(`/api/v1/tms/filter-profiles/${profileId}`);
  return res.data;
}

export async function tmsAgentRunNow(configId: number) {
  const res = await api.post(`/api/v1/tms/providers/${configId}/agent/run-now`);
  return res.data;
}

export async function tmsAgentDryRun(configId: number) {
  const res = await api.post(`/api/v1/tms/providers/${configId}/agent/dry-run`);
  return res.data;
}

export async function getTmsSyncLogAudit(
  logId: number,
  params?: { action?: string; outcome?: string; limit?: number },
) {
  const res = await api.get(`/api/v1/tms/sync-logs/${logId}/audit`, { params });
  return res.data;
}

export async function listTmsQuarantine(params?: { limit?: number }) {
  const res = await api.get('/api/v1/tms/agent/quarantine', { params });
  return res.data;
}

// ─── Trip Summary / Shift Summary Notes ─────────────────────────────────────

export async function createDebriefNote(driverId: string, data: { load_id: string; note_text: string }) {
  const res = await api.post(`/api/v1/analytics/drivers/${driverId}/notes`, data);
  return res.data;
}

export async function listDriverNotes(driverId: string, params?: {
  page?: number; page_size?: number; search?: string;
  date_from?: string; date_to?: string; load_id?: string;
}) {
  const res = await api.get(`/api/v1/analytics/drivers/${driverId}/notes`, { params });
  return res.data;
}

export async function listLoadNotes(loadId: string, params?: { page?: number; page_size?: number }) {
  const res = await api.get(`/api/v1/analytics/loads/${loadId}/notes`, { params });
  return res.data;
}

// ─── HOS (Hours of Service) ──────────────────────────────────────────────────

export async function listHosCountries() {
  const res = await api.get('/api/v1/admin/hos/countries');
  return res.data;
}

export async function createHosCountry(data: object) {
  const res = await api.post('/api/v1/admin/hos/countries', data);
  return res.data;
}

export async function updateHosCountry(countryCode: string, data: object) {
  const res = await api.patch(`/api/v1/admin/hos/countries/${countryCode}`, data);
  return res.data;
}

export interface HosRulePackSummary {
  pack_id: string;
  pack_version: string;
  pack_label: string;
  country_code: string;
  applies_to_country_codes: string[];
  subregion_code: string | null;
  rule_count: number;
  recording_modes_accepted: string[];
  cross_border_carry_over: boolean;
  description: string;
}

export async function listHosRulePacks(): Promise<HosRulePackSummary[]> {
  const res = await api.get('/api/v1/hos/rule-packs');
  return res.data;
}

export async function listHosRules(countryCode: string) {
  const res = await api.get(`/api/v1/admin/hos/countries/${countryCode}/rules`);
  return res.data;
}

export async function createHosRule(countryCode: string, data: object) {
  const res = await api.post(`/api/v1/admin/hos/countries/${countryCode}/rules`, data);
  return res.data;
}

export async function updateHosRule(ruleId: string, data: object) {
  const res = await api.patch(`/api/v1/admin/hos/rules/${ruleId}`, data);
  return res.data;
}

export async function deleteHosRule(ruleId: string) {
  const res = await api.delete(`/api/v1/admin/hos/rules/${ruleId}`);
  return res.data;
}

// ─── HOS Compliance ──────────────────────────────────────────────────────────

export async function getHosCompliance(driverId: string, date?: string) {
  const res = await api.get(`/api/v1/analytics/drivers/${driverId}/hos-compliance`, {
    params: date ? { date } : undefined,
  });
  return res.data;
}

// ─── Shift Detection ─────────────────────────────────────────────────────────

export async function getDriverShifts(driverId: string, params?: { date_from?: string; date_to?: string }) {
  const res = await api.get(`/api/v1/analytics/drivers/${driverId}/shifts`, { params });
  return res.data;
}

export async function getShiftDebrief(driverId: string, shiftIndex: number) {
  const res = await api.get(`/api/v1/analytics/drivers/${driverId}/shifts/${shiftIndex}/debrief`);
  return res.data;
}

// ─── Assignments ────────────────────────────────────────────────────────────

export async function getAssignmentLoads(params?: {
  page?: number; page_size?: number; status?: string; assignment?: string;
  driver_id?: string; origin?: string; date_from?: string; date_to?: string;
  search?: string; sort?: string; logistics_group_id?: string;
}) {
  const res = await api.get('/api/v1/assignments/loads', { params });
  return res.data;
}

export async function getEligibleDrivers(loadId: string) {
  const res = await api.get(`/api/v1/assignments/loads/${loadId}/eligible-drivers`);
  return res.data;
}

export async function assignDriverToLoad(loadId: string, data: {
  driver_id: string; override?: boolean; override_reason?: string; notes?: string;
}) {
  const res = await api.post(`/api/v1/assignments/loads/${loadId}/assign`, data);
  return res.data;
}

export async function unassignDriverFromLoad(loadId: string, data?: { notes?: string }) {
  const res = await api.post(`/api/v1/assignments/loads/${loadId}/unassign`, data || {});
  return res.data;
}

export async function reassignDriverToLoad(loadId: string, data: {
  new_driver_id: string; override?: boolean; override_reason?: string; notes?: string;
}) {
  const res = await api.post(`/api/v1/assignments/loads/${loadId}/reassign`, data);
  return res.data;
}

export async function getLoadAssignmentHistory(loadId: string) {
  const res = await api.get(`/api/v1/assignments/loads/${loadId}/history`);
  return res.data;
}

export async function bulkAssignDriver(data: {
  driver_id: string; load_ids: string[]; override?: boolean; override_reason?: string;
}) {
  const res = await api.post('/api/v1/assignments/bulk-assign', data);
  return res.data;
}

export async function getDriverSchedule(driverId: string, params?: { date_from?: string; date_to?: string }) {
  const res = await api.get(`/api/v1/assignments/drivers/${driverId}/schedule`, { params });
  return res.data;
}

export async function fetchTmsLoads(data: {
  statuses?: string[];
  start_datetime?: string;
  end_datetime?: string;
}) {
  const res = await api.post('/api/v1/assignments/fetch-tms-loads', data);
  return res.data;
}

export async function getDriverFutureLoads(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/future-loads`);
  return res.data;
}

export async function getDriverAuditLog(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/audit-log`);
  return res.data;
}

// ─── Superadmin ──────────────────────────────────────────────────────────────

export async function listAccounts(params?: { search?: string; page?: number; page_size?: number }) {
  return api.get('/api/v1/superadmin/accounts', { params });
}

export async function createAccount(data: object) {
  return api.post('/api/v1/superadmin/accounts', data);
}

export async function getAccount(id: string) {
  return api.get(`/api/v1/superadmin/accounts/${id}`);
}

export async function updateAccount(id: string, data: object) {
  return api.patch(`/api/v1/superadmin/accounts/${id}`, data);
}

export async function activateAccount(id: string) {
  return api.post(`/api/v1/superadmin/accounts/${id}/activate`);
}

export async function deactivateAccount(id: string) {
  return api.post(`/api/v1/superadmin/accounts/${id}/deactivate`);
}

export async function getAccountUsage(id: string) {
  return api.get(`/api/v1/superadmin/accounts/${id}/usage`);
}

export async function getPlatformDashboard() {
  return api.get('/api/v1/superadmin/dashboard');
}

export async function getAccountUsers(id: string) {
  return api.get(`/api/v1/superadmin/accounts/${id}/users`);
}

export async function createAccountUser(id: string, data: object) {
  return api.post(`/api/v1/superadmin/accounts/${id}/users`, data);
}

export async function onboardAccount(id: string, data: object) {
  return api.post(`/api/v1/superadmin/accounts/${id}/onboard`, data);
}

// ─── Driver Work Schedule ────────────────────────────────────────────────────
export async function getDriverWorkSchedule(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/work-schedule`);
  return res.data;
}

export async function setDriverWorkSchedule(driverId: string, data: { schedules: object[] }) {
  const res = await api.put(`/api/v1/admin/drivers/${driverId}/work-schedule`, data);
  return res.data;
}

export async function bulkSetWorkSchedule(data: { driver_ids: string[]; schedules: object[] }) {
  const res = await api.post('/api/v1/admin/drivers/bulk-work-schedule', data);
  return res.data;
}

// ─── Driver Holidays ─────────────────────────────────────────────────────────
export async function getDriverHolidays(driverId: string, year?: number) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/holidays`, {
    params: year ? { year } : undefined,
  });
  return res.data;
}

export async function addDriverHoliday(driverId: string, data: object) {
  const res = await api.post(`/api/v1/admin/drivers/${driverId}/holidays`, data);
  return res.data;
}

export async function deleteDriverHoliday(driverId: string, holidayId: string) {
  const res = await api.delete(`/api/v1/admin/drivers/${driverId}/holidays/${holidayId}`);
  return res.data;
}

export async function bulkAddHolidays(data: { driver_ids: string[]; holidays: object[] }) {
  const res = await api.post('/api/v1/admin/drivers/bulk-holidays', data);
  return res.data;
}

// ─── Equipment Types ─────────────────────────────────────────────────────────
export async function listEquipmentTypes(includeInactive = false) {
  const res = await api.get('/api/v1/admin/equipment-types', {
    params: includeInactive ? { include_inactive: true } : undefined,
  });
  return res.data;
}

export async function createEquipmentType(data: object) {
  const res = await api.post('/api/v1/admin/equipment-types', data);
  return res.data;
}

export async function updateEquipmentType(id: string, data: object) {
  const res = await api.patch(`/api/v1/admin/equipment-types/${id}`, data);
  return res.data;
}

// ─── Driver Equipment Eligibility ────────────────────────────────────────────
export async function getDriverEquipmentEligibility(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/equipment-eligibility`);
  return res.data;
}

export async function setDriverEquipmentEligibility(driverId: string, data: { equipment_type_ids: string[] }) {
  const res = await api.put(`/api/v1/admin/drivers/${driverId}/equipment-eligibility`, data);
  return res.data;
}

// ─── Driver Availability ─────────────────────────────────────────────────────
export async function checkDriverAvailability(driverId: string, date: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/availability`, {
    params: { date },
  });
  return res.data;
}

// ─── AI Assignment Rules ────────────────────────────────────────────────────
export async function getAssignmentRules(ruleSetId?: string) {
  const params: Record<string, string> = {};
  if (ruleSetId) params.rule_set_id = ruleSetId;
  const res = await api.get('/api/v1/ai-assignment/rules', { params });
  return res.data;
}

export async function createAssignmentRule(data: {
  rule_set_id: string;
  name: string;
  description?: string;
  category: string;
  is_hard_constraint: boolean;
  weight: number;
  custom_rule_text: string;
}) {
  const res = await api.post('/api/v1/ai-assignment/rules', data);
  return res.data;
}

export async function updateAssignmentRule(ruleId: string, data: Record<string, unknown>) {
  const res = await api.put(`/api/v1/ai-assignment/rules/${ruleId}`, data);
  return res.data;
}

export async function deleteAssignmentRule(ruleId: string) {
  const res = await api.delete(`/api/v1/ai-assignment/rules/${ruleId}`);
  return res.data;
}

export async function reorderAssignmentRules(items: { rule_id: string; priority: number }[]) {
  const res = await api.put('/api/v1/ai-assignment/rules/reorder', { items });
  return res.data;
}

export async function getAIRecommendations(loadIds: string[], ruleSetId?: string) {
  const res = await api.post('/api/v1/ai-assignment/recommend', {
    load_ids: loadIds,
    rule_set_id: ruleSetId || undefined,
  }, { timeout: 120000 });
  return res.data;
}

// ── Rule Set API ──
export async function getRuleSets(activeOnly = false) {
  const res = await api.get('/api/v1/ai-assignment/rule-sets', {
    params: { active_only: activeOnly }
  });
  return res.data;
}

export async function getRuleSetDetail(ruleSetId: string) {
  const res = await api.get(`/api/v1/ai-assignment/rule-sets/${ruleSetId}`);
  return res.data;
}

export async function createRuleSet(data: {
  logistics_group_id: string; name: string; description?: string;
}) {
  const res = await api.post('/api/v1/ai-assignment/rule-sets', data);
  return res.data;
}

export async function updateRuleSet(ruleSetId: string, data: {
  name?: string; description?: string; logistics_group_id?: string;
}) {
  const res = await api.put(`/api/v1/ai-assignment/rule-sets/${ruleSetId}`, data);
  return res.data;
}

export async function deactivateRuleSet(ruleSetId: string) {
  const res = await api.post(`/api/v1/ai-assignment/rule-sets/${ruleSetId}/deactivate`);
  return res.data;
}

export async function activateRuleSet(ruleSetId: string) {
  const res = await api.post(`/api/v1/ai-assignment/rule-sets/${ruleSetId}/activate`);
  return res.data;
}

export async function duplicateRuleSet(ruleSetId: string, data: {
  new_name: string; description?: string; logistics_group_id?: string;
}) {
  const res = await api.post(`/api/v1/ai-assignment/rule-sets/${ruleSetId}/duplicate`, data);
  return res.data;
}

export async function getRuleSetPreference(logisticsGroupId?: string) {
  const res = await api.get('/api/v1/ai-assignment/rule-set-preference', {
    params: { logistics_group_id: logisticsGroupId }
  });
  return res.data;
}

export async function submitAIAssignments(assignments: { load_id: string; driver_id: string }[]) {
  const res = await api.post('/api/v1/ai-assignment/submit', { assignments });
  return res.data;
}

// ── Driver Debriefer ────────────────────────────────────────────────────────

export async function getDebriefRuleSets() {
  const res = await api.get('/api/v1/debriefer/rule-sets');
  return res.data;
}

export async function getDebriefRuleSet(ruleSetId: string) {
  const res = await api.get(`/api/v1/debriefer/rule-sets/${ruleSetId}`);
  return res.data;
}

export async function updateDebriefRuleSet(ruleSetId: string, data: {
  rules?: unknown[];
  notify_on_red?: boolean;
  daily_token_budget?: number;
  name?: string;
}) {
  const res = await api.put(`/api/v1/debriefer/rule-sets/${ruleSetId}`, data);
  return res.data;
}

export async function resetDebriefRuleSetDefaults(ruleSetId: string) {
  const res = await api.post(`/api/v1/debriefer/rule-sets/${ruleSetId}/reset-defaults`);
  return res.data;
}

export async function getTripDebrief(loadId: string) {
  const res = await api.get(`/api/v1/debriefer/trips/${loadId}/debrief`);
  return res.data;
}

export async function runTripDebrief(loadId: string) {
  const res = await api.post(`/api/v1/debriefer/trips/${loadId}/debrief/run`);
  return res.data;
}

export async function setTripDebriefFeedback(loadId: string, feedback: 'AGREE' | 'DISAGREE') {
  const res = await api.post(`/api/v1/debriefer/trips/${loadId}/debrief/feedback`, { feedback });
  return res.data;
}

export async function listTripDebriefs(params?: {
  status?: 'GREEN' | 'AMBER' | 'RED';
  from?: string;
  to?: string;
  limit?: number;
}) {
  const res = await api.get('/api/v1/debriefer/trips', { params });
  return res.data;
}

// ── Messaging & Notifications ──────────────────────────────────────────────

export async function sendMessage(data: {
  recipient_id?: string | null;
  load_id?: string | null;
  parent_message_id?: string | null;
  message_text: string;
  message_type?: string;
  metadata?: Record<string, unknown>;
}) {
  const res = await api.post('/api/v1/messaging/messages', data);
  return res.data;
}

export async function getMessages(params?: {
  load_id?: string;
  driver_id?: string;
  page?: number;
  page_size?: number;
}) {
  const res = await api.get('/api/v1/messaging/messages', { params });
  return res.data;
}

export async function markMessageRead(messageId: string) {
  const res = await api.post(`/api/v1/messaging/messages/${messageId}/read`);
  return res.data;
}

export async function getNotifications(params?: {
  unread_only?: boolean;
  page?: number;
  page_size?: number;
}) {
  const res = await api.get('/api/v1/messaging/notifications', { params });
  return res.data;
}

export async function getUnreadNotificationCount() {
  const res = await api.get('/api/v1/messaging/notifications/unread-count');
  return res.data;
}

export async function markNotificationsRead(notificationIds: string[] | null) {
  const res = await api.post('/api/v1/messaging/notifications/mark-read', {
    notification_ids: notificationIds,
  });
  return res.data;
}

export async function registerFcmToken(token: string) {
  const res = await api.post('/api/v1/auth/fcm-token', { token });
  return res.data;
}

// ── Facility Management ──────────────────────────────────────────────────

export async function getFacilities(params?: { search?: string; type?: string; active?: boolean; geofenceStatus?: string; sortBy?: string; sortDir?: string; offset?: number; limit?: number; includeGeojson?: boolean }) {
  const res = await api.get('/api/v1/facilities', { params });
  return res.data;
}

export async function getFacility(id: string) {
  const res = await api.get(`/api/v1/facilities/${id}`);
  return res.data;
}

export async function getFacilityStats() {
  const res = await api.get('/api/v1/facilities/stats');
  return res.data;
}

export async function createFacility(body: Record<string, unknown>) {
  const res = await api.post('/api/v1/facilities', body);
  return res.data;
}

export async function updateFacility(id: string, body: Record<string, unknown>) {
  const res = await api.patch(`/api/v1/facilities/${id}`, body);
  return res.data;
}

export async function deactivateFacility(id: string) {
  const res = await api.post(`/api/v1/facilities/${id}/deactivate`);
  return res.data;
}

export async function reactivateFacility(id: string) {
  const res = await api.post(`/api/v1/facilities/${id}/reactivate`);
  return res.data;
}

export async function resolveFacilityGeofence(id: string) {
  const res = await api.post(`/api/v1/facilities/${id}/resolve-geofence`);
  return res.data;
}

export async function resolveAllFacilityGeofences() {
  const res = await api.post('/api/v1/facilities/resolve-all-geofences');
  return res.data;
}

export async function setFacilityGeofence(id: string, geojsonPolygon: object, bufferMeters?: number) {
  const res = await api.patch(`/api/v1/facilities/${id}/geofence`, {
    geojson_polygon: geojsonPolygon, buffer_meters: bufferMeters ?? null,
  });
  return res.data;
}

export async function clearFacilityGeofence(id: string) {
  const res = await api.delete(`/api/v1/facilities/${id}/geofence`);
  return res.data;
}

export async function getFacilityGeofenceHistory(id: string) {
  const res = await api.get(`/api/v1/facilities/${id}/geofence/history`);
  return res.data;
}

export async function importFacilityGeofence(id: string, geojson: object, bufferMeters?: number) {
  const res = await api.post(`/api/v1/facilities/${id}/geofence/import`, {
    geojson, source_format: 'geojson', buffer_meters: bufferMeters ?? null,
  });
  return res.data;
}

// Legacy aliases for backward compat
export const getFacilityGeofences = getFacilities;
export const getFacilityGeofenceStats = getFacilityStats;
export const reResolveFacilityGeofence = resolveFacilityGeofence;

// ── Facility Driver Authorization ────────────────────────────────────────

export async function getFacilityExcludedDrivers(facilityId: string) {
  const res = await api.get(`/api/v1/facilities/${facilityId}/excluded-drivers`);
  return res.data;
}

export async function addFacilityExcludedDriver(facilityId: string, body: object) {
  const res = await api.post(`/api/v1/facilities/${facilityId}/excluded-drivers`, body);
  return res.data;
}

export async function removeFacilityExcludedDriver(facilityId: string, driverId: string) {
  const res = await api.delete(`/api/v1/facilities/${facilityId}/excluded-drivers/${driverId}`);
  return res.data;
}

export async function updateFacilityExcludedDrivers(facilityId: string, body: object) {
  const res = await api.put(`/api/v1/facilities/${facilityId}/excluded-drivers`, body);
  return res.data;
}

export async function getFacilityPreferredDrivers(facilityId: string) {
  const res = await api.get(`/api/v1/facilities/${facilityId}/preferred-drivers`);
  return res.data;
}

export async function addFacilityPreferredDriver(facilityId: string, body: object) {
  const res = await api.post(`/api/v1/facilities/${facilityId}/preferred-drivers`, body);
  return res.data;
}

export async function removeFacilityPreferredDriver(facilityId: string, driverId: string) {
  const res = await api.delete(`/api/v1/facilities/${facilityId}/preferred-drivers/${driverId}`);
  return res.data;
}

export async function updateFacilityPreferredDrivers(facilityId: string, body: object) {
  const res = await api.put(`/api/v1/facilities/${facilityId}/preferred-drivers`, body);
  return res.data;
}

export async function getDriverFacilityExclusions(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/facility-exclusions`);
  return res.data;
}

export async function getDriverFacilityPreferences(driverId: string) {
  const res = await api.get(`/api/v1/admin/drivers/${driverId}/facility-preferences`);
  return res.data;
}

export async function setDriverFacilityExclusions(driverId: string, body: object) {
  const res = await api.put(`/api/v1/admin/drivers/${driverId}/facility-exclusions`, body);
  return res.data;
}

export async function setDriverFacilityPreferences(driverId: string, body: object) {
  const res = await api.put(`/api/v1/admin/drivers/${driverId}/facility-preferences`, body);
  return res.data;
}

// ── AI Observations ─────────────────────────────────────────────────────────

export async function getAIBriefing(forceRefresh = false) {
  const res = await api.get('/api/v1/ai-observations/briefing', {
    params: { force_refresh: forceRefresh },
    timeout: 30000,
  });
  return res.data;
}

export async function getAIObservationsHealth() {
  const res = await api.get('/api/v1/ai-observations/health');
  return res.data;
}

export async function draftDelayEmail(loadId: string, stopId: string) {
  const res = await api.post('/api/v1/ai-observations/delay-draft', {
    load_id: loadId,
    stop_id: stopId,
  }, { timeout: 30000 });
  return res.data;
}

export async function sendDelayEmail(data: {
  load_id: string; stop_id: string; subject: string;
  body_text: string; body_html: string; recipient_emails: string[];
}) {
  const res = await api.post('/api/v1/ai-observations/send-delay-email', data);
  return res.data;
}

// ── Email Provider Config ────────────────────────────────────────────────────

export interface EmailProviderConfig {
  id: string;
  account_id: string;
  provider: string;
  smtp_host: string;
  smtp_port: number;
  encryption: string;
  username: string;
  password_masked: string;
  from_name: string;
  from_email: string;
  enabled: boolean;
  last_test_at: string | null;
  last_test_status: string | null;
  last_test_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailProviderConfigInput {
  provider: string;
  smtp_host?: string;
  smtp_port?: number;
  encryption?: string;
  username: string;
  password: string;
  from_name: string;
  from_email: string;
  enabled?: boolean;
}

export interface EmailTestResult {
  success: boolean;
  message: string;
  error_detail?: string | null;
}

export async function getEmailConfig(): Promise<EmailProviderConfig | null> {
  const res = await api.get('/api/v1/admin/email-config');
  return res.data;
}

export async function upsertEmailConfig(data: EmailProviderConfigInput): Promise<EmailProviderConfig> {
  const res = await api.put('/api/v1/admin/email-config', data);
  return res.data;
}

export async function updateEmailConfig(data: Partial<EmailProviderConfigInput>): Promise<EmailProviderConfig> {
  const res = await api.patch('/api/v1/admin/email-config', data);
  return res.data;
}

export async function deleteEmailConfig(): Promise<void> {
  await api.delete('/api/v1/admin/email-config');
}

export async function sendTestEmail(recipientEmail: string): Promise<EmailTestResult> {
  const res = await api.post('/api/v1/admin/email-config/test', { recipient_email: recipientEmail });
  return res.data;
}

// ── Logistics Groups ─────────────────────────────────────────────────────────

export async function getMyLogisticsGroups(): Promise<{id: string; group_code: string; group_name: string}[]> {
  const res = await api.get('/api/v1/admin/logistics-groups/my-groups');
  return res.data;
}

export async function getLogisticsGroups() {
  const res = await api.get('/api/v1/admin/logistics-groups');
  return res.data;
}

export async function getLogisticsGroupStats() {
  const res = await api.get('/api/v1/admin/logistics-groups/stats');
  return res.data;
}

export async function createLogisticsGroup(data: {
  group_code: string;
  group_name: string;
  description?: string;
  timezone?: string;
  unit_of_measure?: string;
  currency?: string;
  driver_can_stop_tracking?: boolean;
  tracking_auto_stop_enabled?: boolean;
}) {
  const res = await api.post('/api/v1/admin/logistics-groups', data);
  return res.data;
}

export async function getLogisticsGroup(id: string) {
  const res = await api.get(`/api/v1/admin/logistics-groups/${id}`);
  return res.data;
}

export async function updateLogisticsGroup(id: string, data: {
  group_name?: string;
  description?: string;
  timezone?: string;
  driver_can_stop_tracking?: boolean;
  tracking_auto_stop_enabled?: boolean;
}) {
  const res = await api.patch(`/api/v1/admin/logistics-groups/${id}`, data);
  return res.data;
}

export async function deactivateLogisticsGroup(id: string) {
  const res = await api.post(`/api/v1/admin/logistics-groups/${id}/deactivate`);
  return res.data;
}

export async function reactivateLogisticsGroup(id: string) {
  const res = await api.post(`/api/v1/admin/logistics-groups/${id}/reactivate`);
  return res.data;
}

export async function addGroupMember(groupId: string, data: { user_id: string; is_primary?: boolean }) {
  const res = await api.post(`/api/v1/admin/logistics-groups/${groupId}/members`, data);
  return res.data;
}

export async function removeGroupMember(groupId: string, userId: string) {
  const res = await api.delete(`/api/v1/admin/logistics-groups/${groupId}/members/${userId}`);
  return res.data;
}

export async function assignGroupFacility(groupId: string, data: { facility_id: string }) {
  const res = await api.post(`/api/v1/admin/logistics-groups/${groupId}/facilities`, data);
  return res.data;
}

export async function unassignGroupFacility(groupId: string, facilityId: string) {
  const res = await api.delete(`/api/v1/admin/logistics-groups/${groupId}/facilities/${facilityId}`);
  return res.data;
}

// ─── GDPR / Privacy ─────────────────────────────────────────────────────────

export async function anonymizeDriver(driverId: string, reason: string, confirm: boolean) {
  const res = await api.post(`/api/v1/admin/drivers/${driverId}/anonymize`, { reason, confirm });
  return res.data;
}

export async function restrictProcessing(driverId: string, restricted: boolean, reason?: string) {
  const res = await api.post(`/api/v1/admin/drivers/${driverId}/restrict-processing`, { restricted, reason });
  return res.data;
}

export async function exportDriverData(driverId: string, format: string = 'json') {
  const res = await api.post(`/api/v1/admin/drivers/${driverId}/data-export`, { format });
  return res.data;
}

export async function getRetentionPolicies() {
  const res = await api.get('/api/v1/admin/gdpr/retention-policies');
  return res.data;
}

export async function updateRetentionPolicy(id: string, data: { retention_days?: number; action?: string; is_active?: boolean }) {
  const res = await api.patch(`/api/v1/admin/gdpr/retention-policies/${id}`, data);
  return res.data;
}

export async function getPrivacyStatus() {
  const res = await api.get('/api/v1/auth/privacy-status');
  return res.data;
}

export async function getDPIATemplate() {
  const res = await api.get('/api/v1/admin/gdpr/dpia-template');
  return res.data;
}

export async function getAnonymizationLog() {
  const res = await api.get('/api/v1/admin/gdpr/anonymization-log');
  return res.data;
}

export async function getExportLog() {
  const res = await api.get('/api/v1/admin/gdpr/export-log');
  return res.data;
}

export async function getPrivacyDashboard() {
  const res = await api.get('/api/v1/admin/gdpr/privacy-dashboard');
  return res.data;
}

// ── Proof of Delivery ─────────────────────────────────────────────────────────

export async function resendPodEmail(stopId: string, recipientEmail?: string) {
  const res = await api.post(`/api/v1/pods/stops/${stopId}/resend-email`, {
    recipient_email: recipientEmail || undefined,
  });
  return res.data;
}

export async function downloadPodPdf(stopId: string) {
  const res = await api.get(`/api/v1/pods/stops/${stopId}/download-pdf`, {
    responseType: 'blob',
  });
  // Trigger browser download
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `POD-${stopId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}


// ── Load Schedule Cascade ────────────────────────────────────────────────────

export interface CascadeAffectedLoad {
  load_id: string;
  load_number: string;
  driver_id: string | null;
  driver_name: string | null;
  planned_start: string | null;
  planned_end: string | null;
  revised_start: string | null;
  revised_end: string | null;
  revised_at: string | null;
  shift_minutes: number;
  source_load_id: string | null;
  source_load_number: string | null;
  /** True when the cascade revision was driven by HOS (cumulative day
   *  drive would push past the smallest break trigger in the driver's
   *  rule pack). Set by load_cascade_service when persisting revisions. */
  hos_reason?: boolean;
  /** Cached projection signal on the cascade-shifted load. */
  hos_traffic_light?: 'GREEN' | 'AMBER' | 'RED' | null;
}

export interface CascadeRecommendationOption {
  action_type: string;
  params: Record<string, unknown>;
  confidence: number | null;
  rationale: string | null;
  predicted_impact: string | null;
}

export interface CascadeRecommendation {
  id: string;
  load_id: string | null;
  load_number: string | null;
  context: Record<string, unknown>;
  ai_options: CascadeRecommendationOption[];
  ai_model: string | null;
  ai_generated_at: string;
  user_action: Record<string, unknown> | null;
  user_action_by_id: string | null;
  user_action_at: string | null;
  created_at: string;
}

export interface CascadeListItem {
  affected_load: CascadeAffectedLoad;
  pending_recommendation: CascadeRecommendation | null;
}

export async function listCascades(): Promise<CascadeListItem[]> {
  const res = await api.get('/api/v1/cascades');
  return res.data.items ?? [];
}

export async function getCascadeHistory(loadId: string) {
  const res = await api.get(`/api/v1/cascades/${loadId}/history`);
  return res.data;
}

export async function captureCascadeDecision(loadId: string, payload: {
  recommendation_id: string;
  chosen_option_index: number;
  action_type: string;
  params?: Record<string, unknown>;
  free_text?: string;
}) {
  const res = await api.post(`/api/v1/cascades/${loadId}/decision`, payload);
  return res.data as CascadeRecommendation;
}

export async function clearCascadeRevision(loadId: string) {
  await api.post(`/api/v1/cascades/${loadId}/clear`);
}


export default api;

// ─── Typed modules (WP-P1) ──────────────────────────────────────────────────
// New domain modules live in ./api/*.ts and are re-exported here so both
// `import { getFeatures } from 'services/api'` and
// `import { getFeatures } from 'services/api/features'` work.
export * from './api/index';
